import { apiClient } from '@services/api-client'
import { SNA_PREFIX } from '@services/utils'
import { useQuery } from '@tanstack/react-query'
import type { ChatList, UserProfile } from 'types/api'
import type { BaseResponse, RoleAccess } from 'types/utils'

type useChatListProps<Role extends UserProfile['role']> = {
  limit?: number
  offset?: number
} & (
  | RoleAccess<
      Role,
      {
        isActive?: boolean
        offer?: string
        has_unread?: boolean
      }
    >
  | {}
)

export const chatsQueryKey = ['chats']

export function useChatList<Role extends UserProfile['role']>(
  props: useChatListProps<Role>,
  enabled = true
) {
  async function getChats() {
    if ('user' in props) {
      const response = await apiClient.get<BaseResponse<ChatList>>(
        SNA_PREFIX +
          `chats?${new URLSearchParams({
            limit: props.limit?.toString() || '10',
            offset: props.offset?.toString() || '0',
            is_active: props.isActive?.toString() || '',
            user: props.user?.toString() || '',
            offer: props.offer?.toString() || '',
            has_unread: props.has_unread?.toString() || ''
          }).toString()}`
      )
      return response.data
    } else {
      const meParams: Record<string, string> = {
        limit: props.limit?.toString() || '10',
        offset: props.offset?.toString() || '0'
      }
      const meIsActive = (props as { isActive?: boolean }).isActive
      if (meIsActive !== undefined) meParams.is_active = meIsActive.toString()

      const response = await apiClient.get<BaseResponse<ChatList>>(
        SNA_PREFIX + `chats/me?${new URLSearchParams(meParams).toString()}`
      )
      return response.data
    }
  }

  // Build query key based on discriminated union
  const queryKey =
    'user' in props
      ? [
          ...chatsQueryKey,
          'list',
          props.isActive,
          props.limit,
          props.offset,
          props.user,
          props.offer,
          props.has_unread
        ]
      : [
          ...chatsQueryKey,
          'me',
          props.limit,
          props.offset,
          (props as { isActive?: boolean }).isActive
        ]

  return useQuery({
    queryFn: getChats,
    queryKey,
    enabled
    // refetchInterval: 5000
  })
}
